import type { EntityPluginContext } from "@brains/plugins";
import { fetchStyleGuide, formatVoiceGuidance } from "@brains/contracts";
import { z } from "@brains/utils/zod";

const generatePostInputSchema = z.object({
  prompt: z.string(),
  seriesName: z.string().optional(),
});

const generateExcerptInputSchema = z.object({
  title: z.string(),
  content: z.string(),
});

type GeneratePostInput = z.output<typeof generatePostInputSchema>;
type GenerateExcerptInput = z.output<typeof generateExcerptInputSchema>;

export function registerEvalHandlers(context: EntityPluginContext): void {
  context.eval.registerHandler("generatePost", async (input: unknown) => {
    const parsed: GeneratePostInput = generatePostInputSchema.parse(input);
    const generationPrompt = `${parsed.prompt}${parsed.seriesName ? `\n\nNote: This is part of a series called "${parsed.seriesName}".` : ""}`;

    const voiceGuidance = formatVoiceGuidance(
      await fetchStyleGuide(context.entityService),
    );
    return context.ai.generate(
      {
        prompt: generationPrompt,
        templateName: "blog:generation",
        representedIdentity: "anchor",
        ...(voiceGuidance && { styleGuide: { voice: voiceGuidance } }),
      },
      z.object({
        title: z.string(),
        content: z.string(),
        excerpt: z.string(),
      }),
    );
  });

  context.eval.registerHandler("generateExcerpt", async (input: unknown) => {
    const parsed: GenerateExcerptInput =
      generateExcerptInputSchema.parse(input);

    const voiceGuidance = formatVoiceGuidance(
      await fetchStyleGuide(context.entityService),
    );
    return context.ai.generate(
      {
        prompt: `Title: ${parsed.title}\n\nContent:\n${parsed.content}`,
        templateName: "blog:excerpt",
        representedIdentity: "none",
        ...(voiceGuidance && { styleGuide: { voice: voiceGuidance } }),
      },
      z.object({ excerpt: z.string() }),
    );
  });
}
